// Чистые хелперы упрочнения клиентской эмиссии событий (kill-stats #9 / фикс блокера W3 291598).
// БЕЗ связи с DOM/стором → юнит-тестируются `node --test` (см. client_events.test.mjs).
//
// Блокер 291598 (разбор — deploy/README.md): `_eid` (идемпотентный ключ подхода) живёт ВНУТРИ
// ячейки доски в `game.data`; целостная подмена доски (afterSave `set('game', data)` / onSocket
// `mergeDeep`) теряет свежесозданный `_eid` → повторный ввод минтит НОВЫЙ `_eid` → дубликат визита
// в клиентской тени (event_compare structural). Теневая карта координата→`_eid` переживает подмену
// доски и восстанавливает исходный `_eid` при повторном вводе → сервер дедупит по idempotency_key.

export function makeEidLedger() {
  return new Map();
}

// Координата подхода: лег × серия × сторона. `:` разделяет, чтобы многозначные индексы не слипались.
export function coordKey(leg, series, side) {
  return `${leg}:${series}:${side}`;
}

// Единый резолв клиентского события для координаты `key`. Возвращает {cellEid, priorEid, undoTarget}
// и мутирует карту. Ключевой инвариант: КЛОББЕР (целостная подмена доски) стирает `_eid` ИЗ ЯЧЕЙКИ,
// поэтому и correction, и undo нельзя определять по `cellEid` — оба ПАДАЮТ на запомненный в карте `_eid`.
//
//  hasScore (заполнение ячейки):
//   • есть prior (cellEid ЛИБО карта помнит) → это СУЩЕСТВУЮЩИЙ визит координаты → эмитим CORRECTION
//     (priorEid=prior, свой свежий _eid у события), восстанавливаем `_eid` ячейки = prior. Работает и для
//     тождественного реплея (no-op correction), и для изменённого значения (правка) — проекция == доска.
//     Реюз-как-visit НЕ используем: сервер задедупил бы его по idempotency_key и потерял бы правку.
//   • prior нет → действительно НОВЫЙ визит: минтим, запоминаем, priorEid=undefined (buildClientEvent → visit).
//  !hasScore (очистка/undo):
//   • target = undoTargetEid (из ячейки) ЛИБО запомненный в карте (клоббер стёр `_eid`) → эмитим undo(target)
//     и ЗАБЫВАЕМ координату (повторный ввод потом = НОВЫЙ визит). Нечего забывать → no-op.
export function resolveEmission(ledger, key, cellEid, hasScore, undoTargetEid, mint) {
  if (hasScore) {
    const prior = cellEid || ledger.get(key);
    if (prior) {
      ledger.set(key, prior);
      return {cellEid: prior, priorEid: prior, undoTarget: undefined};
    }
    const fresh = mint();
    ledger.set(key, fresh);
    return {cellEid: fresh, priorEid: undefined, undoTarget: undefined};
  }
  const target = undoTargetEid || ledger.get(key);
  if (target) ledger.delete(key);
  return {cellEid: cellEid, priorEid: undefined, undoTarget: target};
}

// ─── ОТКАЗ АВТОРИТЕТНОГО ПИСАТЕЛЯ: ЛЕДЖЕР НЕ ОТКАТЫВАЕМ (решение ревью-промпта 5, 01.08.2026) ──
//
// Ревью предлагало на неудачном POST'е «откатывать запись в леджере, чтобы повторный ввод шёл как
// `visit`, а не как сиротская `correction`». **Отвергнуто, и вот почему.**
//
// Леджер помнит `_eid` ровно потому, что клиент НЕ ЗНАЕТ, применился ли запрос: сеть могла оборваться
// ПОСЛЕ коммита сервера. Откати мы запись — повторный ввод уехал бы НОВЫМ визитом, и в
// append-ленте, из которой удалить нечем, подход задвоился бы. То есть «починка» меняет тихую потерю
// на тихое задвоение — класс тот же, знак другой.
//
// Правильный ответ лежит уровнем выше и уже принят: сервер, который не записал, отвечает НЕ 200
// (`views._writer_failed`), клиент повторяет ТОТ ЖЕ батч с ТЕМ ЖЕ `_eid` (идемпотентно), а до конца
// повтора ввод заморожен (`status !== 'stable'` гасит клавиатуру). Сиротская `correction` при этом
// становится недостижимой штатным путём, а если всё же придёт — сервер отвечает 422 «перезагрузите»,
// и перезагрузка обнуляет леджер вместе с вкладкой. Разбор — `deploy/README.md`.

// Стоит ли ПОВТОРИТЬ тот же батч автоматически, не тревожа игрока.
//
// ⚠ Только 503 и только он: это НАШ код, которым `views._writer_failed` говорит «писатель споткнулся
// (лок/таймаут/БД), состояние не тронуто, повтор поможет». 422 повторять бессмысленно (тело всегда
// будет то же), 400/409 — тем более (партия заблокирована / контракт сменился). Любой другой 5xx
// повторять НЕ начинаем: чужие ошибки не идемпотентны по определению, и молчаливый повтор там —
// способ применить одно и то же дважды.
export function shouldAutoRetryStatus(status) {
  return status === 503;
}

// Что игроку делать с отказом: повторить, перезагрузить или ничего. Отсюда берётся текст модалки
// и кнопка.
//
// ⚠ Дефолт — 'retry', и это осознанная асимметрия. Ошибка, которой мы не знаем, скорее транзиентная
// (шлюз, прокси, разрыв), а цена неверной догадки разная: лишний повтор идемпотентен по `_eid`, а
// лишнее «перезагрузите страницу» посреди матча — потерянный ввод и раздражённый игрок.
//
// ⚑ 400 — СВОЙ вид, а не «reload» (ревью 02.08.2026). Единственный 400 этого эндпоинта —
// `views.py` `if game.live_blocked` (запись заморожена через час после конца партии). У корзины
// 'reload' текст «последний подход НЕ СОХРАНЁН — обновите страницу» и кнопка перезагрузки; для 400
// ложны ОБА утверждения: сервер до записи не дошёл, терять было нечего, партия закрыта навсегда, а
// перезагрузка возвращает тот же экран — следующий тап даёт тот же 400. Радиус — все форматы и все
// партии старше часа, потому что 400 приходит из ветки `post()`, заведённой задолго до трека.
export function transportErrorKind(status) {
  if (status === 400) return 'blocked';
  return (status === 409 || status === 422) ? 'reload' : 'retry';
}

// Что делает КНОПКА модалки для каждого вида отказа. Вынесено сюда, а не написано в компоненте,
// по правилу трека «у условия ОДИН автор»: рядом с `transportErrorKind` расхождение видно глазами,
// а в JSX его не проверить тестом (React в Node не поднимается).
//
// 'dismiss' = просто закрыть модалку. Для закрытой партии это ЕДИНСТВЕННОЕ честное действие:
// повторять нечего, перезагружать бессмысленно.
export function errorAction(kind) {
  if (kind === 'reload') return 'reload';
  if (kind === 'blocked') return 'dismiss';
  return 'retry';
}

// Бот играет ТОЛЬКО по устоявшемуся дереву: статус stable И очередь запросов пуста. Иначе бот
// планировал бы бросок по возможно устаревшему снимку доски (усугубитель 291598).
export function botGateOpen(status, queueLength) {
  return status === 'stable' && queueLength === 0;
}

// ⚑ КЛАВИАТУРА ГАСНЕТ ПО ДРУГОМУ, БОЛЕЕ МЯГКОМУ УСЛОВИЮ — и расхождение с `botGateOpen` намеренное
// (решение владельца 02.08.2026, после замера на живом экране).
//
// Одиночный летящий запрос ввод НЕ гасит. Прежде гасил — и это давало серую клавиатуру после КАЖДОГО
// подхода: замер на dev показал, что серое окно равно кругу запроса один в один (980/974 мс холодный,
// 403/395 мс второй). На x01 это самый нагруженный экран продукта, и владелец такую цену отверг:
// «моргание серой клавиатурой очень мешает».
//
// Потерю ввода это НЕ открывает, потому что лечится она в другом месте и надёжнее: `restoreSelected`
// перестал затирать набранное (см. его комментарий). Гард нужен ровно на два оставшихся случая,
// где принимать ввод нельзя:
//   • `status !== 'stable'` — на экране модалка отказа либо идёт авто-повтор;
//   • `queueLength > 1` — запросы СТАКАЮТСЯ (второй встал в очередь за первым).
// Второе условие в норме избыточно (`api.js::request` при непустой очереди сам поднимает 'loading'),
// но `getVideoToken` кладёт запрос в очередь МИМО `request()`, не трогая статус, — без явной проверки
// очереди этот случай остался бы незакрытым.
export function inputGateOpen(status, queueLength) {
  return status === 'stable' && queueLength <= 1;
}

// ─── W4 предусловие 2: СИММЕТРИЧНЫЙ жизненный цикл (завершение И его откат) ───────────────
// До W4 гард эмиссии был односторонним (ловил только falsy→truthy) — откат завершения ретракт НЕ
// эмитил. Пока жизненный цикл берётся из legacy-доски, это безвредно; на W4 он поедет из клиентской
// ленты, и откатанная игра осталась бы «завершённой» навсегда.
//
// `prevEnd` — `end_time` в сторе (состояние ДО сохранения), `body` — тело сейва.
// Ключ `end_time` обязан ПРИСУТСТВОВАТЬ в теле: `Game.save({with_video: true})` на завершённой игре
// не несёт `end_time`, и трактовать его отсутствие как откат нельзя — посторонний сейв гасил бы терминал.
export function lifecycleTransition(prevEnd, body) {
  if (!body || !('end_time' in body)) return null;
  if (body.end_time && !prevEnd) return 'finish';
  if (!body.end_time && prevEnd) return 'undo_finish';
  return null;
}

export function buildFinishEvent(eid) {
  return {_eid: eid, type: 'finish'};
}

// Ретракт терминала. `finishEid` (запомненный `_eid` события finish этой вкладки) даёт ТОЧНУЮ цель.
// Его нет — вкладку перезагрузили (поле живёт только в памяти модели), а кнопка «Отменить бросок»
// после reload доступна ⇒ ретракт всё равно обязан уйти: просим сервер найти ЭФФЕКТИВНЫЙ терминал
// ленты сам (`target_kind: 'finish'`, разбирает `client_ingest._normalize`).
export function buildFinishUndoEvent(eid, finishEid) {
  return finishEid
    ? {_eid: eid, type: 'undo', _eid_target: finishEid}
    : {_eid: eid, type: 'undo', target_kind: 'finish'};
}

// Дедуп ДВОЙНОГО preSave: `sendSeries` зовёт `preSave` сам, а потом `api.save()` зовёт его ещё раз
// поверх результата — без дедупа откат завершения дал бы ДВА ретракта. Ключ дедупа — цель: ретракт
// ПРОШЛОГО терминала (другой `_eid_target`) не имеет права погасить эмиссию ретракта текущего
// (сценарий finish→откат→finish→откат).
export function hasFinishUndo(events, finishEid) {
  if (!Array.isArray(events)) return false;
  return events.some(e => e && e.type === 'undo' && (
    finishEid ? e._eid_target === finishEid : e.target_kind === 'finish'
  ));
}

// ─── W3.5: СТРУКТУРНЫЕ метки — действия, которые до сих пор ехали ТОЛЬКО доской ───────────
// Три кнопки меняют `stats`, но НЕ порождают ни одного подхода, поэтому лента о них молчала и была
// структурно БЕДНЕЕ доски: «Смена хода» (Menu) -> дыра `first_stale` 7.7% игр; «След. лег»/«Пред. лег»
// (ModalEndLeg/Menu) -> дыра `empty_tail` 2.5%. Материализация `stats` из проекции (W4) на такой ленте
// теряла бы данные. Разбор — `specs/to-be/migrations/_kill-stats-w4-stats-inventory.md`.
//
// ⚠ ЛЕГ 0 КЛИЕНТ НЕ ПОМЕЧАЕТ: он существует с создания игры, кнопкой его никто не открывает. В
// авторитетной ленте метка на него есть (сервер выводит её из доски), в клиентской — нет. Асимметрия
// НАМЕРЕННАЯ: «у матча всегда есть лег 0» — инвариант, а не факт, который надо переносить событием.
// Сверку проекций (`===EVENT-COMPARE===`) это не задевает: движки структурные метки игнорируют.

// ─── W4-время: «игру начали» ───────────────────────────────────────────────────────────────
// Четвёртое доска-онли действие: модалка «Начать» (`ModalIntro`) шлёт `Game.save({start_time})`, и до
// сих пор лента об этом молчала — сервер узнавал о старте только с ПЕРВЫМ БРОСКОМ. Замер W4 §0:
// систематический зазор +23 с (медиана), p90 66 с. Это не дрейф часов, а разная семантика.
//
// ⚠ ПЕРЕХОД ловим ТОЧНО так же, как `end_time` (`lifecycleTransition`), и по той же причине: ключ
// обязан ПРИСУТСТВОВАТЬ в теле. `Game.save({first: pos})` из той же модалки (выбор, кто начинает)
// `start_time` не несёт — трактуй мы его отсутствие как «старт отменили», выбор стороны гасил бы
// метку старта.
export function startTransition(prevStart, body) {
  if (!body || !('start_time' in body)) return null;
  if (body.start_time && !prevStart) return 'start';
  if (!body.start_time && prevStart) return 'undo_start';
  return null;
}

export function buildGameStartEvent(eid) {
  return {_eid: eid, type: 'game_start'};
}

// Ретракт старта. `startEid` (запомненный `_eid` метки ЭТОЙ вкладки) даёт ТОЧНУЮ цель; его нет —
// вкладку перезагрузили (поле живёт только в памяти модели) ⇒ просим сервер найти эффективную метку
// сам (`target_kind: 'game_start'`). Полная симметрия с ретрактом терминала и открытия лега.
export function buildGameStartUndoEvent(eid, startEid) {
  return startEid
    ? {_eid: eid, type: 'undo', _eid_target: startEid}
    : {_eid: eid, type: 'undo', target_kind: 'game_start'};
}

// Дедуп ретракта старта — зеркало `hasFinishUndo`: `preSave` зовётся ДВАЖДЫ на одном сейве
// (`sendSeries` сам + `api.save` поверх), без дедупа ушли бы два ретракта.
export function hasGameStartUndo(events, startEid) {
  if (!Array.isArray(events)) return false;
  return events.some(e => e && e.type === 'undo' && (
    startEid ? e._eid_target === startEid : e.target_kind === 'game_start'
  ));
}

export function buildSetStarterEvent(eid, side) {
  return {_eid: eid, type: 'set_starter', side};
}

export function buildLegOpenEvent(eid, leg) {
  return {_eid: eid, type: 'leg_open', leg};
}

// ─── шаг event-native COUNTUP: ПЯТОЕ доска-онли действие — ручной тайбрейк НИЧЬЕЙ лега ────────
// `ModalDraw.onWinner` пишет `leg[leg.length-1][side].winner = true` ВНУТРЬ доски и шлёт
// `Game.save({data, score_1, score_2})`, минуя `sendSeries` ⇒ ноль событий. Пока доску хранил
// браузер, это работало; на событийном пути флаг терялся бы весь.
//
// ⚠ Флаг несёт СМЫСЛ ИНТЕРФЕЙСА, а не только факт игры: экран выбирает модалку по
// `endLeg = !!getWinnerNumberByLeg(leg)`, а тот для ничейного лега смотрит РОВНО на этот флаг. Потеряй
// его серверная доска — `endLeg` снова ложен, `isDraw` всё ещё истина (суммы равны), и модалка «Ничья»
// вернулась бы сразу после ответа сервера. Ровно класс сентинела `darts: null` у x01.
export function buildLegWinnerEvent(eid, leg, side) {
  return {_eid: eid, type: 'leg_winner', leg, side};
}

// Ретракт присуждения («Отменить бросок»/«Отменить серию» стирают флаг у ОБЕИХ сторон).
// `legWinnerEid` (запомненный `_eid` метки ЭТОЙ вкладки) даёт ТОЧНУЮ цель; его нет — вкладку
// перезагрузили (карта живёт только в памяти модели), а кнопка после reload доступна ⇒ просим сервер
// найти последнюю непогашенную метку сам (`target_kind: 'leg_winner'`, разбирает
// `client_ingest._normalize`). Четвёртый случай ОДНОГО класса — как finish / leg_open / game_start.
export function buildLegWinnerUndoEvent(eid, legWinnerEid) {
  return legWinnerEid
    ? {_eid: eid, type: 'undo', _eid_target: legWinnerEid}
    : {_eid: eid, type: 'undo', target_kind: 'leg_winner'};
}

// Ретракт открытия лега («Пред. лег»). `legOpenEid` (запомненный `_eid` метки ЭТОЙ вкладки) даёт ТОЧНУЮ
// цель. Его нет — вкладку перезагрузили (карта живёт только в памяти модели), а кнопка после reload
// доступна ⇒ ретракт всё равно обязан уйти: сервер найдёт последнюю непогашенную метку сам
// (`target_kind: 'leg_open'`, разбирает `client_ingest._normalize`). Полная симметрия с ретрактом
// терминала — тот же класс дефекта и то же лечение.
export function buildLegCloseEvent(eid, legOpenEid) {
  return legOpenEid
    ? {_eid: eid, type: 'undo', _eid_target: legOpenEid}
    : {_eid: eid, type: 'undo', target_kind: 'leg_open'};
}

// Диагностические поля клиентского события (справочно, санитизируются на сервере client_ingest):
// actor помечает источник (bot/human — критично для локализации бот-класса блокера 291598),
// client_seq — монотонный порядковый номер, client_ts — клиентское время (мс epoch).
export function buildEventMeta(botActing, seq, nowMs) {
  return {actor: botActing ? 'bot' : 'human', client_seq: seq, client_ts: nowMs};
}

// ─── kill-stats A+B+C (кусок C): СНЯТИЕ ДОСКА-ПОСТА ────────────────────────────────────────
// Ключи, которые составляют ДОСКУ в теле POST'а. Ровно они — предмет шага: после него их пишет
// СЕРВЕР из ленты (`stats_materialize.WRITTEN_KEYS`), а браузер перестаёт их слать.
export const BOARD_KEYS = ['data', 'first', 'start_time', 'end_time'];

// Признак «эта игра event-native» приходит ПОЛЕМ В ТЕЛЕ ОТВЕТА, а не глобальной переменной шаблона.
// ⚠ Так сделано намеренно, и вот почему шаблонная переменная тут не годится:
//   • признак ПО-ИГРОВОЙ (зависит от формата, режима, водораздела, rollout-строки) — две вкладки
//     одного браузера законно дают разные ответы, а `window.*` одна на страницу;
//   • глобально включённый признак выключил бы отправку доски и там, где ленты нет вовсе
//     (countup/cricket/online/командные) — это ПОТЕРЯ ДАННЫХ, а не деградация;
//   • откат: `response_game` зовётся на каждом GET/POST/broadcast, поэтому снятие repo var
//     действует со СЛЕДУЮЩЕГО ответа — а шаблонная переменная впечена в отрендеренную страницу и
//     до уже открытой вкладки не доедет никогда.
// Сравнение строгое (`=== true`): fail-closed. Любое «почти истинное» значение (строка, 1,
// undefined после клоббера стора) обязано означать «шлём доску, как раньше».
export function isEventNative(game) {
  return !!game && game.event_native === true;
}

// Снять доску с тела POST'а. Зовётся ПОСЛЕДНИМ шагом `preSave` — уже ПОСЛЕ того, как события
// жизненного цикла выведены из `body` (`lifecycleTransition`/`startTransition` смотрят на
// присутствие ключей `end_time`/`start_time`). Порядок обязателен: сними доску раньше — и переходы
// «игру начали» / «партию завершили» перестали бы определяться вовсе, то есть лента потеряла бы
// терминал и метку старта.
//
// Клиентская модель доски при этом НЕ трогается: браузер по-прежнему считает её у себя, чтобы
// рисовать экран немедленно (оптимистично). Меняется только то, ЧТО уезжает на сервер.
export function stripBoardKeys(body) {
  const out = {};
  for (const k of Object.keys(body || {})) {
    if (!BOARD_KEYS.includes(k)) out[k] = body[k];
  }
  return out;
}


// Сентинел «сколько дротиков закрыл»: `getDataAndScore` ставит `darts: null` на клетку, закрывшую
// лег без секторной разбивки, а модалка `DartsClosure` показывается ровно по `ss.darts === null`.
// Это ВОПРОС игроку, а не значение. До снятия доска-поста он ехал доской; теперь обязан ехать
// событием, иначе серверная материализация вернёт доску без него и модалка мигнёт и исчезнет.
export function dartsPending(ss) {
  return !!ss && ss.darts === null;
}


// Индекс дротика, в который пишется бросок. `selected[3]` равен null, пока в клетке нет ни одного
// дротика (`getSelected` оставляет его null у НЕ НАЧАТОЙ клетки, когда advanced_input выключен) —
// а писать по такому индексу нельзя: `sectors[null] = ...` создаёт СТРОКОВОЕ свойство, длина
// массива остаётся 0, сумма очков выходит undefined, и подход не порождает ни очков, ни события.
//
// ⚠ Раньше это чинилось САМО, через доску: клетка `{sectors: []}` уезжала на сервер и возвращалась,
// после чего `getSelected` видел `sectors` и выдавал 0 — бот бросал со второй попытки. После снятия
// доска-поста доска приходит ИЗ ЛЕНТЫ, а пустой клетки в ленте нет, и самопочинка исчезла: бот
// зацикливался, переигрывая один и тот же бросок. Правило вынесено сюда, чтобы `getSelected` и
// `sendSector` судили об одном факте ОДИНАКОВО — расхождение двух мест уже стоило треку дорого.
export function nextShotIndex(sectors, selectedShot) {
  if (Number.isInteger(selectedShot)) return selectedShot;
  const list = Array.isArray(sectors) ? sectors : [];
  const free = list.findIndex((s) => !s);
  if (free !== -1) return free;
  return list.length >= 3 ? 2 : list.length;
}


// ─── шаг event-native CRICKET: ГЕЙТ ЭМИССИИ И СБОРКА ИГРОВОГО СОБЫТИЯ ───────────────────────────
//
// ⚑ ПОЧЕМУ ЭТО ЗДЕСЬ, А НЕ В `base.js`. Решение «эмитить ли подход и что он несёт» — ОБЩИЙ код, на
// котором стоит прод-x01. До этого шага гейтом был `ss.score !== undefined`, и он МОЛЧА исключал
// всё семейство cricket: его клетка скаляра не несёт вовсе (`inputSS = {darts: 0, sectors: []}`,
// `cricket.js`), поэтому cricket не эмитил НИ ОДНОГО игрового события — клиентской тени у него не
// «мало», а РОВНО НОЛЬ строк (замер прода 2026-07-31 и 2026-08-01). Заодно клетка не получала `_eid`,
// и доска-писатель ключевал её позиционно.
//
// Вынесено чистыми функциями по уроку №2 трека («дублированное условие тесты не ловят»): условие
// выбирается ОДИН раз, юнит-тестируется `node --test` и не имеет второго автора.


// «В клетке есть полезная нагрузка» — единственный гейт эмиссии игрового события.
//
// ⚠⚠ ПРОВЕРЯЕМ `!== undefined`, А НЕ TRUTHINESS. `score: 0` — ЛЕГАЛЬНЫЙ подход x01 («ничего не
// попал»), и соблазнительное `if (ss.score || ss.sectors)` молча перестало бы его эмитить. Трек на
// этой ловушке уже спотыкался дважды (`nextShotIndex`, `sectors[null]`).
//
// ⚠ Пустой `sectors: []` нагрузкой НЕ считается, и это не придирка: у x01 так выглядит клетка, с
// которой сняли все дротики, а у cricket — ввод до первой метки. Серверная лента такую клетку тоже
// пропускает (`ingest._filled_cells` cricket-ветка: `if not sectors: continue`), значит гейт клиента
// и гейт сервера согласны по составу клеток.
export function hasPayload(ss) {
  if (!ss) return false;
  if (ss.score !== undefined) return true;
  return Array.isArray(ss.sectors) && ss.sectors.length > 0;
}


// Очки подхода для события. У x01/countup их несёт скаляр `score` (клиент считает сам, и пересчёт
// по секторам был бы НЕВЕРЕН: при переборе/недоборе клетка законно несёт `score`, не равный сумме
// дротиков). У cricket скаляра нет — очки = Σ shot×m по МЕТКАМ ПОПАДАНИЙ.
//
// ⚑ Формула обязана быть ТОЖДЕСТВЕННА серверной (`ingest._filled_cells`, cricket-ветка:
// `sum(d[0]*d[1] for d in sectors if ... and d[0])`). Разойдись они — `declared_score` события и
// доска-диффа отличались бы, и офлайн-реплей сравнивал бы два разных кодирования одного подхода.
// Метки с нулевым СЕКТОРОМ (`[0, 0]` — добивка до трёх) пропускаем, как сервер.
export function visitScore(ss) {
  if (!ss) return undefined;
  if (ss.score !== undefined) return ss.score;
  if (!Array.isArray(ss.sectors)) return undefined;
  return ss.sectors.reduce(
    (sum, d) => (Array.isArray(d) && d.length >= 2 && d[0] ? sum + d[0] * d[1] : sum),
    0,
  );
}


// Полезная нагрузка ИГРОВОГО события (visit/correction) — ровно те поля, что читает сервер
// (`client_ingest._normalize`). `null` = событию быть не должно (вызывающий решает про undo).
export function buildVisitPayload(ss, side, leg) {
  if (!hasPayload(ss)) return null;
  const out = {side, leg, score: visitScore(ss)};
  if (ss.sectors) out.sectors = ss.sectors;
  if (ss.darts !== undefined && ss.darts !== null) out.darts = ss.darts;
  // Сентинел «вопрос о числе дротиков задан, ответа нет». Едет отдельным полем, а не `darts: null`,
  // чтобы не путаться с «дротиков ноль» и не трогать семантику движка.
  if (dartsPending(ss)) out.darts_pending = true;
  return out;
}


// ─── КАНОНИЧЕСКАЯ ФОРМА КЛЕТКИ CRICKET ─────────────────────────────────────────────────────────
// `cricket.js::preSave` добивает метки до трёх (`[0, 0]`) и ставит `darts = 3`. Это НЕ косметика:
//   • `cricket.js::getSelected` читает `leg[i]?.[p]?.sectors?.length` — ДЛИНА массива это состояние
//     ИНТЕРФЕЙСА (куда встанет курсор), а не факт игры;
//   • `base.js::getDarts` читает `darts` ПЕРВЫМ и лишь при его отсутствии падает на `sectors.length`.
//
// ⚑ ПОЧЕМУ ПРАВИЛО ПЕРЕЕХАЛО СЮДА. Событие собирается в `sendSeries`, а `preSave` зовётся ПОСЛЕ —
// значит в ленту уехал бы `darts: 0` из `inputSS`, тогда как в доску сервер получил бы `darts: 3`.
// Разойтись им нельзя ровно по причине выше: `darts` это вход курсора. Теперь у правила ОДИН автор,
// и оба вызывающих (`preSave` и `sendSeries`) зовут его.
//
// ⇒ РЕШЕНИЕ ШАГА (§3.2 промпта, спрашивалось явно): да, добивка — ОБЯЗАННОСТЬ канонической формы, а
// не случайность `preSave`. Значит и материализация из ленты обязана вернуть клетку добитой: метки
// уезжают в событие уже добитыми.
//
// Мутирует переданную клетку (она же лежит в доске стора) и возвращает её — как `preSave` и делал.
export function normalizeMarksCell(ss) {
  if (ss && Array.isArray(ss.sectors) && ss.sectors.length) {
    while (ss.sectors.length < 3) ss.sectors.push([0, 0]);
    // По присутствию КЛЮЧА, а не truthiness: клетки старой формы (`{score, sectors}`, 7431 на проде)
    // ключа не несут, и выдумывать его нельзя — fail-closed, как на серверной ветке.
    if (ss.darts !== undefined) ss.darts = 3;
  }
  return ss;
}
